// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                                       from 'react';
import { ReactSVG }                     from 'react-svg';

// ===== Components =====

import {
    OutsideClickHandler,
}                                       from '../Editor/helpers';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
}                                       from '../../enums';

// ===== Services =====

import {
    playAudio,
    detectTouchDevice,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
}                                       from '../../services';
import useLazyAudioRef                           from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    IDropdownItem,
    IDropdownOptionTouchStart,
}                                       from '../../interfaces';

// ===== Images =====

import PixelAsteriskIcon                from '../../images/8-bit_asterisk.svg';
import PixelCrossIcon                   from '../../images/8-bit_cross.svg';

// ===== Sounds =====

import ButtonClick                      from '../../sounds/button_click.mp3';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    DEFAULT_AUDIO_VOLUME,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';
import KEYCODE                          from '../../constants/keycodes';

// ===== Styles =====

import {
    Container,
    DropdownHeading,
    SelectOption,
    SelectButton,
    SelectedValue,
    SelectArrow,
    SelectOptionInput,
    SelectOptionLabel,
    SelectDropdown,
    ArrowContainer,
    RequiredIndicatorContainer,
    UnselectButton,
    DropdownContainer,
}                                       from './styles';

const TOUCH_SCROLL_INTENT_THRESHOLD_PX = 8;
const SUPPRESS_MOUSE_AFTER_TOUCH_MS = 700;

PixelDropdown.defaultProps = {
    isRequired: false,
    isValid: true,
    noValuesMessage: 'No values',
};
interface Props {
    customCursorActive: boolean,
    isRequired?: boolean,
    isValid?: boolean,
    hasSound: boolean,
    currentValue: IDropdownItem | undefined,
    values: IDropdownItem[],
    placeholderText: string,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    onChange: (value: IDropdownItem | undefined) => void,
    noValuesMessage?: string,
}
function PixelDropdown({
    customCursorActive,
    isRequired,
    isValid,
    hasSound,
    currentValue,
    values,
    placeholderText,
    noValuesMessage,
    onCursorEnter,
    onCursorLeave,
    onChange,
}: Props): JSX.Element {
    // ===== Refs =====

    // ----- Sound Clips

    const buttonClickClip = useLazyAudioRef({
        src: ButtonClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const selectButtonRef = useRef<HTMLButtonElement>(null);
    const selectedValueRef = useRef<HTMLSpanElement>(null);
    const selectDropdownRef = useRef<HTMLUListElement>(null);
    // Tracks whether Cmd/Ctrl+A was used so next keypress can replace/clear all text in one action.
    const selectAllActiveRef = useRef<boolean>(false);
    const dropdownOptionTouchStartRef = useRef<IDropdownOptionTouchStart | null>(null);
    const dropdownOptionTouchMovedRef = useRef<boolean>(false);
    const lastDropdownOptionTouchTimestampRef = useRef<number>(0);

    // ===== State =====

    const [dropdownIsVisible, setDropdownIsVisible] = useState<boolean>(false);
    const [dropdownIsFocused, setDropdownIsFocused] = useState<boolean>(false);
    const [typeaheadQuery, setTypeaheadQuery] = useState<string>('');
    const [typeaheadMatchIndex, setTypeaheadMatchIndex] = useState<number>(-1);

    // ===== Methods =====

    const onDropdownEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.dropdown,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onDropdownLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onDropdownUnselectButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.dropdownUnselectButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onDropdownUnselectButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onDropdownChange = (value: IDropdownItem | undefined, e: React.MouseEvent | React.TouchEvent): void => {
        e.stopPropagation();
        onChange(value);
        setDropdownIsVisible(false);
        setTypeaheadQuery('');
        setTypeaheadMatchIndex(-1);
        selectAllActiveRef.current = false;
        onCursorLeave();
    };

    const updateLastDropdownOptionTouchTimestamp = (): void => {
        lastDropdownOptionTouchTimestampRef.current = Date.now();
    };

    /**
     * Finds the active touch for this option interaction so a scroll gesture
     * can move across child elements without being mistaken for a tap.
     */
    const findDropdownOptionTouch = (
        touches: React.TouchList,
        identifier?: number,
    ): React.Touch | undefined => {
        for (let index = 0; index < touches.length; index += 1) {
            const touch = touches.item(index);
            if (
                touch
                && (identifier === undefined || touch.identifier === identifier)
            ) {
                return touch;
            }
        }

        return undefined;
    };

    const resetDropdownOptionTouch = (): void => {
        dropdownOptionTouchStartRef.current = null;
        dropdownOptionTouchMovedRef.current = false;
    };

    const touchMovedPastScrollThreshold = (e: React.TouchEvent): boolean => {
        const touchStart = dropdownOptionTouchStartRef.current;
        if (!touchStart) return false;

        const touch = findDropdownOptionTouch(e.changedTouches, touchStart.identifier);
        if (!touch) return false;

        const horizontalDistance = Math.abs(touch.clientX - touchStart.clientX);
        const verticalDistance = Math.abs(touch.clientY - touchStart.clientY);
        return (
            horizontalDistance > TOUCH_SCROLL_INTENT_THRESHOLD_PX
            || verticalDistance > TOUCH_SCROLL_INTENT_THRESHOLD_PX
        );
    };

    const onDropdownOptionTouchStart = (e: React.TouchEvent): void => {
        e.stopPropagation();
        updateLastDropdownOptionTouchTimestamp();

        const touch = findDropdownOptionTouch(e.changedTouches);
        if (!touch) {
            resetDropdownOptionTouch();
            return;
        }

        dropdownOptionTouchStartRef.current = {
            identifier: touch.identifier,
            clientX: touch.clientX,
            clientY: touch.clientY,
        };
        dropdownOptionTouchMovedRef.current = false;
    };

    const onDropdownOptionTouchMove = (e: React.TouchEvent): void => {
        e.stopPropagation();
        updateLastDropdownOptionTouchTimestamp();

        if (touchMovedPastScrollThreshold(e)) {
            dropdownOptionTouchMovedRef.current = true;
        }
    };

    const onDropdownOptionTouchEnd = (
        value: IDropdownItem,
        e: React.TouchEvent,
    ): void => {
        e.stopPropagation();
        updateLastDropdownOptionTouchTimestamp();

        const touchWasScrollIntent = (
            dropdownOptionTouchMovedRef.current
            || touchMovedPastScrollThreshold(e)
        );
        resetDropdownOptionTouch();

        if (touchWasScrollIntent) return;

        onDropdownChange(value, e);
    };

    const onDropdownOptionTouchCancel = (e: React.TouchEvent): void => {
        e.stopPropagation();
        updateLastDropdownOptionTouchTimestamp();
        resetDropdownOptionTouch();
    };

    const onDropdownOptionMouseDown = (
        value: IDropdownItem,
        e: React.MouseEvent,
    ): void => {
        e.stopPropagation();
        const recentTouchInteraction = (
            Date.now() - lastDropdownOptionTouchTimestampRef.current < SUPPRESS_MOUSE_AFTER_TOUCH_MS
        );
        if (recentTouchInteraction) return;

        onDropdownChange(value, e);
    };

    const onDropdownFocus = (): void => {
        if (!dropdownIsFocused) setDropdownIsFocused(true);
        setDropdownIsVisible(true);

        // Play Sound
        if (hasSound && buttonClickClip.current) {
            buttonClickClip.current.pause();
            buttonClickClip.current.currentTime = 0;
            playAudio(buttonClickClip.current);
        }
    };

    const onDropdownBlur = (): void => {
        if (dropdownIsFocused) setDropdownIsFocused(false);
        if (dropdownIsVisible) setDropdownIsVisible(false);
        setTypeaheadQuery('');
        setTypeaheadMatchIndex(-1);
        selectAllActiveRef.current = false;
    };

    const focusDropdownOption = (value: IDropdownItem): void => {
        if (!dropdownIsVisible) return;
        const nextValueElement = document.getElementById(`pixel-dropdown-option=${value.value}`)
            || document.getElementById(`pixel-dropdown=${value.value}`);
        const dropdownElement = selectDropdownRef.current;
        if (!nextValueElement || !dropdownElement) return;

        // Keep scrolling scoped to the dropdown list itself to avoid shifting the page viewport.
        const optionTop = (nextValueElement as HTMLElement).offsetTop;
        const optionHeight = (nextValueElement as HTMLElement).offsetHeight;
        const targetScrollTop = optionTop - ((dropdownElement.clientHeight - optionHeight) / 2);
        const maxScrollTop = Math.max(0, dropdownElement.scrollHeight - dropdownElement.clientHeight);
        dropdownElement.scrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
    };

    const setDropdownValueByIndex = (index: number): void => {
        const nextValue = values[index];
        if (!nextValue) return;
        onChange(nextValue);
    };

    const findTypeaheadMatchIndex = (
        query: string,
        startIndex = 0,
    ): number => {
        // Empty query (or no options) means there is nothing meaningful to match.
        if (query.length === 0 || values.length === 0) return -1;
        const normalizedQuery = query.toLowerCase();
        // Exact match always wins (e.g., "united states" should beat "united states minor ...").
        let exactMatchIndex = -1;
        // Collect prefix candidates so we can rank them deterministically afterward.
        const prefixMatches: Array<{
            index: number,
            nameLength: number,
            distanceFromStart: number,
        }> = [];

        for (let index = 0; index < values.length; index += 1) {
            const candidateName = `${values[index].name || ''}`.trim().toLowerCase();
            if (candidateName === normalizedQuery) {
                exactMatchIndex = index;
                break;
            }
            if (candidateName.startsWith(normalizedQuery)) {
                prefixMatches.push({
                    index,
                    // Shorter option text is usually the more specific/intended target.
                    nameLength: candidateName.length,
                    // Circular distance keeps keyboard navigation stable from the current scan origin.
                    distanceFromStart: (index - startIndex + values.length) % values.length,
                });
            }
        }

        // Return exact match immediately when present.
        if (exactMatchIndex >= 0) return exactMatchIndex;
        // No exact or prefix candidates found.
        if (prefixMatches.length === 0) return -1;

        // Ranking:
        // 1) shortest prefix candidate
        // 2) nearest to start index (preserves predictable directional behavior)
        prefixMatches.sort((left, right) => {
            if (left.nameLength !== right.nameLength) {
                return left.nameLength - right.nameLength;
            }
            return left.distanceFromStart - right.distanceFromStart;
        });

        // Top-ranked candidate becomes the highlighted/selected match.
        return prefixMatches[0].index;
    };

    const onDropdownKeyDown = (e: React.KeyboardEvent): void => {
        if (
            (e.metaKey || e.ctrlKey)
            && e.key.toLowerCase() === 'a'
        ) {
            e.preventDefault();
            // Keep select-all scoped to dropdown text so browser doesn't select the whole page.
            if (selectedValueRef.current) {
                const selection = window.getSelection();
                if (selection) {
                    const range = document.createRange();
                    range.selectNodeContents(selectedValueRef.current);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
            selectAllActiveRef.current = true;
            return;
        }
        const hadSelectAll = selectAllActiveRef.current;

        if (e.key === KEYCODE.escape) {
            e.preventDefault();
            setDropdownIsVisible(false);
            setTypeaheadQuery('');
            setTypeaheadMatchIndex(-1);
            selectButtonRef.current?.blur();
            selectAllActiveRef.current = false;
            return;
        }

        if (e.key === KEYCODE.tab) {
            if (dropdownIsVisible) {
                setDropdownIsVisible(false);
            }
            setTypeaheadQuery('');
            setTypeaheadMatchIndex(-1);
            selectAllActiveRef.current = false;
            return;
        }

        if (e.key === KEYCODE.arrowdown) {
            // Cancel the default action, if needed
            e.preventDefault();
            setTypeaheadQuery('');
            setTypeaheadMatchIndex(-1);
            selectAllActiveRef.current = false;

            if (!currentValue) {
                setDropdownValueByIndex(0);
            } else {
                const currentValueIndex = values.findIndex((item) => item.value === currentValue.value);
                const nextValueIndex = Math.min(currentValueIndex + 1, values.length - 1);
                setDropdownValueByIndex(nextValueIndex);
            }
            return;
        }

        if (e.key === KEYCODE.arrowup) {
            // Cancel the default action, if needed
            e.preventDefault();
            setTypeaheadQuery('');
            setTypeaheadMatchIndex(-1);
            selectAllActiveRef.current = false;

            if (!currentValue) {
                setDropdownValueByIndex(0);
            } else {
                const currentValueIndex = values.findIndex((item) => item.value === currentValue.value);
                const nextValueIndex = Math.max(currentValueIndex - 1, 0);
                setDropdownValueByIndex(nextValueIndex);
            }
            return;
        }

        if (e.key === KEYCODE.enter) {
            e.preventDefault();
            e.stopPropagation();
            selectAllActiveRef.current = false;
            // Commit match only on Enter to avoid expensive parent updates on every character.
            if (
                typeaheadQuery.length > 0
                && typeaheadMatchIndex >= 0
                && typeaheadMatchIndex < values.length
            ) {
                onChange(values[typeaheadMatchIndex]);
                setDropdownIsVisible(false);
                setTypeaheadQuery('');
                setTypeaheadMatchIndex(-1);
                return;
            }

            setDropdownIsVisible(!dropdownIsVisible);
            return;
        }

        if (e.key === KEYCODE.backspace) {
            e.preventDefault();
            if (hadSelectAll) {
                // Cmd/Ctrl+A + Backspace clears all typed text in one keystroke.
                setTypeaheadQuery('');
                setTypeaheadMatchIndex(-1);
                window.getSelection()?.removeAllRanges();
                selectAllActiveRef.current = false;
                return;
            }
            selectAllActiveRef.current = false;
            const nextQuery = typeaheadQuery.slice(0, -1);
            setTypeaheadQuery(nextQuery);
            if (nextQuery.length === 0) {
                setTypeaheadMatchIndex(-1);
                return;
            }

            const nextValueIndex = findTypeaheadMatchIndex(nextQuery);
            if (nextValueIndex === -1) {
                // Preserve unmatched text so user can continue editing toward a valid option.
                setTypeaheadMatchIndex(-1);
                return;
            }

            setDropdownIsVisible(true);
            setTypeaheadMatchIndex(nextValueIndex);
            return;
        }

        if (
            e.key.length === 1
            && !e.altKey
            && !e.ctrlKey
            && !e.metaKey
        ) {
            // Keep typed query local/visible while highlighting the best list candidate.
            e.preventDefault();
            window.getSelection()?.removeAllRanges();
            const typedCharacter = e.key.toLowerCase();
            const nextQueryBase = hadSelectAll ? '' : typeaheadQuery;
            const nextQuery = `${nextQueryBase}${typedCharacter}`;
            const nextValueIndex = findTypeaheadMatchIndex(nextQuery);
            const resolvedQuery = nextQuery;
            selectAllActiveRef.current = false;

            if (nextValueIndex === -1) {
                // Keep the typed query visible even when there is no match.
                setTypeaheadQuery(nextQuery);
                setTypeaheadMatchIndex(-1);
                setDropdownIsVisible(true);
                return;
            }

            setTypeaheadQuery(resolvedQuery);
            setDropdownIsVisible(true);
            setTypeaheadMatchIndex(nextValueIndex);
        }
    };

    const handleDropdownVisibility = (): void => {
        setDropdownIsVisible(!dropdownIsVisible);
    };

    const selectedValueText = (() => {
        if (typeaheadQuery.length > 0) {
            return typeaheadQuery;
        }
        if (!currentValue) {
            return placeholderText;
        }
        return currentValue.name;
    })();

    // ===== Side Effects =====

    useEffect(() => {
        if (!dropdownIsVisible) return;

        const highlightedValue = (
            typeaheadQuery.length > 0
            && typeaheadMatchIndex >= 0
            && typeaheadMatchIndex < values.length
        )
            ? values[typeaheadMatchIndex]
            : currentValue;
        if (!highlightedValue) return;

        // Wait until dropdown visibility/class styles are committed before scrolling an option into view.
        const animationFrameId = window.requestAnimationFrame(() => {
            focusDropdownOption(highlightedValue);
        });

        return () => window.cancelAnimationFrame(animationFrameId);
    }, [
        dropdownIsVisible,
        typeaheadQuery,
        typeaheadMatchIndex,
        currentValue,
        values,
    ]);

    return (
        <OutsideClickHandler
            onClick={() => {
                if (dropdownIsVisible) {
                    setDropdownIsVisible(false);
                }
            }}
        >
            <Container>
                <DropdownContainer
                    isInvalid={!isValid}
                    dropdownIsVisible={dropdownIsVisible}
                    dropdownIsFocused={dropdownIsFocused}
                    valueSelected={!!currentValue}
                    {...(values.length > 0
                        ? {
                            className: HOVER_TARGET_CLASSNAME,
                        } : {})}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: (e) => onDropdownEnter(e),
                        onMouseLeave: (e) => onDropdownLeave(e),
                    })}
                    {...(values.length > 0
                        ? {
                            onMouseDown: handleDropdownVisibility,
                        } : {})}
                >
                    {currentValue && (
                        <DropdownHeading
                            isInvalid={!isValid}
                        >
                            {placeholderText}
                        </DropdownHeading>
                    )}
                    <SelectButton
                        type="button"
                        ref={selectButtonRef}
                        role="combobox"
                        aria-labelledby="select button"
                        aria-haspopup="listbox"
                        aria-expanded={dropdownIsVisible}
                        aria-controls="select-dropdown"
                        customCursorActive={customCursorActive}
                        noValue={!currentValue}
                        isInvalid={!isValid}
                        dropdownIsVisible={dropdownIsVisible}
                        dropdownIsFocused={dropdownIsFocused}
                        onFocus={() => onDropdownFocus()}
                        onBlur={() => onDropdownBlur()}
                        onKeyDown={(e) => onDropdownKeyDown(e)}
                    >
                        {values.length > 0 && (
                            <SelectedValue ref={selectedValueRef}>
                                {selectedValueText}
                            </SelectedValue>
                        )}
                        {values.length === 0 && (<SelectedValue>{noValuesMessage}</SelectedValue>)}
                        <ArrowContainer>
                            {isRequired && (
                                <RequiredIndicatorContainer
                                    isInvalid={!isValid}
                                >
                                    <ReactSVG
                                        src={PixelAsteriskIcon}
                                    />
                                </RequiredIndicatorContainer>
                            )}
                            {values.length > 0 && (<SelectArrow />)}
                        </ArrowContainer>
                    </SelectButton>
                    <SelectDropdown
                        ref={selectDropdownRef}
                        role="listbox"
                    >
                        {values.map((item: IDropdownItem, index: number) => {
                            const optionSelected = typeaheadQuery.length > 0
                                ? index === typeaheadMatchIndex
                                : !!currentValue && currentValue.value === item.value;
                            return (
                                <SelectOption
                                    id={`pixel-dropdown-option=${item.value}`}
                                    customCursorActive={customCursorActive}
                                    selected={optionSelected}
                                    value={item.value}
                                    key={item.value}
                                    role="option"
                                    className={HOVER_TARGET_CLASSNAME}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: (e) => onDropdownEnter(e),
                                        onMouseLeave: (e) => onDropdownLeave(e),
                                    })}
                                    onMouseDown={(e) => onDropdownOptionMouseDown(item, e)}
                                    onTouchStart={(e) => onDropdownOptionTouchStart(e)}
                                    onTouchMove={(e) => onDropdownOptionTouchMove(e)}
                                    onTouchEnd={(e) => onDropdownOptionTouchEnd(item, e)}
                                    onTouchCancel={(e) => onDropdownOptionTouchCancel(e)}
                                >
                                    <SelectOptionInput
                                        type="radio"
                                        id={`pixel-dropdown=${item.value}`}
                                        name={item.value}
                                        checked={optionSelected}
                                        tabIndex={-1}
                                        readOnly
                                    />
                                    <SelectOptionLabel
                                        customCursorActive={customCursorActive}
                                        htmlFor={item.value}
                                    >
                                        {item.name}
                                    </SelectOptionLabel>
                                </SelectOption>
                            );
                        })}
                    </SelectDropdown>
                </DropdownContainer>
                {!!currentValue && (
                    <UnselectButton
                        type="button"
                        customCursorActive={customCursorActive}
                        isInvalid={!isValid}
                        detectTouchDevice={detectTouchDevice(document)}
                        tabIndex={-1}
                        className={HOVER_TARGET_CLASSNAME}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: (e) => onDropdownUnselectButtonEnter(e),
                            onMouseLeave: (e) => onDropdownUnselectButtonLeave(e),
                        })}
                        {...getPressEventHandlers({ onMouseDown: (e) => onDropdownChange(undefined, e) })}
                    >
                        <ReactSVG
                            src={PixelCrossIcon}
                        />
                    </UnselectButton>
                )}
            </Container>
        </OutsideClickHandler>
    );
}

export default PixelDropdown;
